// The playbooks rail's data for this page: the list from /api/playbooks
// (worker/src/playbooks.ts, the operator's Seamless notes), kept in memory for
// the call and refreshed each time the rail opens, and one saver shared by
// every rail on the page. An unsaved body is also copied to this browser's
// storage until Seamless has it, so a closed tab or a crash loses nothing.

import { api } from "./api";
import { PlaybookSaver, bodyToText, overlay, type Playbook } from "./playbooks";

export type ListState = { readonly kind: "loading" } | { readonly kind: "error" } | { readonly kind: "ready"; readonly list: readonly Playbook[] };

const STASH = "breeze:playbook-unsaved:";

let list: Playbook[] | null = null;
let failed = false;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
let version = 0;

function emit(): void {
  version++;
  for (const l of listeners) l();
}

function stash(id: string, body: string | null): void {
  try {
    if (body === null) localStorage.removeItem(STASH + id);
    else localStorage.setItem(STASH + id, body);
  } catch {
    // Kept in memory only.
  }
}

function stashed(id: string): string | null {
  try {
    return localStorage.getItem(STASH + id);
  } catch {
    return null;
  }
}

export const saver = new PlaybookSaver({
  save: (id, body, opts) =>
    api<{ ok: true; updated_at: string }>(`/api/playbooks/${encodeURIComponent(id)}`, { method: "PUT", body: { body }, keepalive: opts.keepalive }),
  stash,
  onChange: emit,
});

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** What the rail shows now; the same object until something changes. */
let shown: { version: number; state: ListState } | null = null;
export function snapshot(): ListState {
  if (shown && shown.version === version) return shown.state;
  const state: ListState = list ? { kind: "ready", list: overlay(list, saver) } : failed ? { kind: "error" } : { kind: "loading" };
  shown = { version, state };
  return state;
}

/** Asks Seamless for the list again; the cached copy stays on screen meanwhile. */
export function refresh(): Promise<void> {
  if (loading) return loading;
  if (!list) {
    failed = false;
    emit();
  }
  let changed = !list || failed;
  loading = api<{ playbooks: Playbook[] }>("/api/playbooks")
    .then((r) => {
      const next = r.playbooks.map((p) => ({ ...p, body: bodyToText(p.body) }));
      if (!list || signature(list) !== signature(next)) changed = true;
      list = next;
      failed = false;
      // A body left unsaved by an earlier visit goes back into the queue.
      for (const p of list) {
        const kept = stashed(p.id);
        if (kept !== null && saver.pendingBody(p.id) === undefined) {
          if (kept === p.body) stash(p.id, null);
          else saver.edit(p.id, kept, p.body);
        }
      }
      if (saver.hasPending()) void saver.flush();
    })
    .catch(() => {
      if (!list) {
        failed = true;
        changed = true;
      }
    })
    .finally(() => {
      loading = null;
      // The rail polls while it is open; a poll that finds nothing new repaints nothing.
      if (changed) emit();
    });
  return loading;
}

function signature(l: readonly Playbook[]): string {
  return JSON.stringify(l.map((p) => [p.id, p.title, p.body]));
}

/** Records an edit and starts its save. */
export function edit(id: string, text: string): void {
  const base = list?.find((p) => p.id === id)?.body ?? "";
  saver.edit(id, text, base);
  void saver.flush();
}

let wired = false;
/** Sends anything unsaved when the tab hides or the page goes away. */
export function wirePageHide(): void {
  if (wired || typeof window === "undefined") return;
  wired = true;
  window.addEventListener("pagehide", () => {
    if (saver.hasPending()) void saver.flush({ keepalive: true });
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && saver.hasPending()) void saver.flush({ keepalive: true });
  });
}
